export const englishCrmMessages = {
  resources: {
    companies: {
      name: "Company |||| Companies",
      forcedCaseName: "Company",
      import: {
        title: "Import customers from Tripletex",
        button: "Import customers",
        hint: "Upload the customer and supplier export from Tripletex (CSV). Customers are matched on customer number: existing customers are updated, new ones are created. The postal address is used, and mobile numbers are preferred over landlines. Archived customers are not changed.",
      },
      fields: {
        name: "Company name",
        website: "Website",
        linkedin_url: "LinkedIn URL",
        phone_number: "Phone number",
        email: "Email",
        created_at: "Created at",
        nb_contacts: "Number of contacts",
        revenue: "Revenue",
        sector: "Sector",
        size: "Size",
        tax_identifier: "Tax Identifier",
        address: "Address",
        city: "City",
        zipcode: "Zip code",
        state_abbr: "State",
        country: "Country",
        description: "Description",
        context_links: "Context links",
        sales_id: "Account manager",
        has_google_review: "Has Google review",
        ask_for_review: "Ask for Google review",
        review_asked_at: "Last asked for review",
      },
      empty: {
        description: "It seems your company list is empty.",
        title: "No companies found",
      },
      field_categories: {
        contact: "Contact",
        additional_info: "Additional information",
        address: "Address",
        context: "Context",
      },
      action: {
        create: "Create Company",
        edit: "Edit company",
        new: "New Company",
        show: "Show company",
      },
      added_on: "Added on %{date}",
      followed_by: "Followed by %{name}",
      followed_by_you: "Followed by you",
      no_contacts: "No contact",
      nb_contacts: "%{smart_count} contact |||| %{smart_count} contacts",
      nb_deals: "%{smart_count} opportunity |||| %{smart_count} opportunities",
      sizes: {
        one_employee: "1 employee",
        two_to_nine_employees: "2-9 employees",
        ten_to_forty_nine_employees: "10-49 employees",
        fifty_to_two_hundred_forty_nine_employees: "50-249 employees",
        two_hundred_fifty_or_more_employees: "250 or more employees",
      },
      autocomplete: {
        create_error: "An error occurred while creating the company",
        create_item: "Create %{item}",
        create_label: "Start typing to create a new company",
      },
      filters: {
        only_mine: "Only companies I manage",
      },
      enrich: {
        button: "Enrich from BRREG",
        title: "Enrich companies from BRREG?",
        content:
          "Looks up every company that has an organization number but no sector in the Brønnøysund Register Centre, and fills in empty fields (sector, size, address). Existing values are never overwritten.",
        success:
          "Enrichment complete: %{updated} updated, %{skipped} skipped, %{notFound} not found in BRREG (%{scanned} scanned).",
        error: "Could not enrich companies from BRREG",
      },
    },
    contacts: {
      name: "Contact |||| Contacts",
      forcedCaseName: "Contact",
      field_categories: {
        background_info: "Background info",
        identity: "Identity",
        misc: "Misc",
        personal_info: "Personal info",
        position: "Position",
      },
      fields: {
        first_name: "First name",
        last_name: "Last name",
        last_seen: "Last seen",
        title: "Title",
        company_id: "Company",
        email_jsonb: "Email addresses",
        email: "Email",
        phone_jsonb: "Phone numbers",
        phone_number: "Phone number",
        linkedin_url: "LinkedIn URL",
        background: "Background info (bio, how you met, etc)",
        has_newsletter: "Has newsletter",
        sales_id: "Account manager",
      },
      action: {
        add: "Add contact",
        add_first: "Add your first contact",
        create: "Create contact",
        edit: "Edit contact",
        export_vcard: "Export to vCard",
        new: "New Contact",
        show: "Show contact",
      },
      background: {
        last_activity_on: "Last activity on %{date}",
        added_on: "Added on %{date}",
        followed_by: "Followed by %{name}",
        followed_by_you: "Followed by you",
        status_none: "None",
      },
      position_at: "%{title} at",
      position_at_company: "%{title} at %{company}",
      empty: {
        description: "It seems your contact list is empty.",
        title: "No contacts found",
      },
      import: {
        title: "Import contacts",
        button: "Import CSV",
        hint: "The file needs a header row. Columns are recognised by name, such as First name, Last name, Company, Email and Phone (Norwegian names work too). Contacts with an email address that already exists are skipped, and missing companies and tags are created.",
        template: "Download template",
      },
      inputs: {
        genders: {
          male: "He/Him",
          female: "She/Her",
          nonbinary: "They/Them",
        },
        personal_info_types: {
          work: "Work",
          home: "Home",
          other: "Other",
        },
      },
      list: {
        error_loading: "Error loading contacts",
      },
      bulk_tag: {
        action: "Tag",
        back: "Back to tags",
        create_description:
          "Create a new tag and apply it to the selected contacts.",
        description:
          "Choose an existing tag or create a new one for the selected contacts.",
        empty: "No tags yet. Create one to tag the selected contacts.",
        error: "Failed to add tag to contacts",
        noop: "Selected contacts already have this tag",
        success:
          "Tag added to %{smart_count} contact |||| Tag added to %{smart_count} contacts",
        title: "Add tag to contacts",
      },
      merge: {
        action: "Merge with another contact",
        confirm: "Merge Contacts",
        current_contact: "Current Contact (will be deleted)",
        description: "Merge this contact with another one.",
        error: "Failed to merge contacts",
        merging: "Merging...",
        no_additional_data: "No additional data to merge",
        select_target: "Please select a contact to merge with",
        success: "Contacts merged successfully",
        target_contact: "Target Contact (will be kept)",
        title: "Merge Contact",
        warning_description:
          "All data will be transferred to the second contact. This action cannot be undone.",
        warning_title: "Warning: Destructive Operation",
        what_will_be_merged: "What will be merged:",
      },
      filters: {
        before_last_month: "Before last month",
        before_this_month: "Before this month",
        before_this_week: "Before this week",
        managed_by_me: "Managed by me",
        search: "Search name, company...",
        this_week: "This week",
        today: "Today",
        tags: "Tags",
        tasks: "Tasks",
      },
      hot: {
        empty_change_status:
          'Change the status of a contact by adding a note to that contact and clicking on "show options".',
        empty_hint: 'Contacts with a "hot" status will appear here.',
        title: "Hot Contacts",
      },
    },
    deals: {
      name: "Opportunity |||| Opportunities",
      fields: {
        name: "Name",
        description: "Description",
        company_id: "Company",
        contact_ids: "Contacts",
        category: "Category",
        amount: "Budget",
        expected_closing_date: "Expected closing date",
        stage: "Stage",
        next_step: "Next step",
        next_step_date: "Next step date",
        sales_id: "Account manager",
        loss_reason: "Loss reason",
        renewal_date: "Renewal date",
      },
      loss_reason: {
        pris_budsjett: "Price/budget",
        timing: "Timing – not now",
        valgte_konkurrent: "Chose competitor",
        ingen_respons: "No response",
        ikke_rett_match: "Not the right fit",
        intern_losning: "In-house / do it themselves",
        lagt_ned: "Project was cancelled",
        annet: "Other",
        annet_migrert: "Other (migrated)",
      },
      action: {
        back_to_deal: "Back to opportunity",
        create: "Create opportunity",
        new: "New opportunity",
      },
      field_categories: {
        misc: "Misc",
      },
      archived: {
        action: "Archive",
        error: "Error: opportunity not archived",
        list_title: "Archived opportunities",
        success: "Opportunity archived",
        title: "Archived opportunity",
        view: "View archived opportunities",
      },
      inputs: {
        linked_to: "Linked to",
      },
      filters: {
        only_mine: "Only opportunities I manage",
      },
      unarchived: {
        action: "Send back to the board",
        error: "Error: opportunity not unarchived",
        success: "Opportunity unarchived",
      },
      updated: "Opportunity updated",
      empty: {
        before_create: "before creating an opportunity.",
        description: "It seems your opportunity list is empty.",
        title: "No opportunities found",
      },
      invalid_date: "Invalid date",
    },
    notes: {
      name: "Note |||| Notes",
      forcedCaseName: "Note",
      fields: {
        status: "Status",
        date: "Date",
        attachments: "Attachments",
        contact_id: "Contact",
        deal_id: "Opportunity",
      },
      action: {
        add: "Add note",
        add_first: "Add your first note",
        delete: "Delete note",
        edit: "Edit note",
        update: "Update note",
        add_this: "Add this note",
      },
      sheet: {
        create: "Create note",
        create_for: "Create note for %{name}",
        edit: "Edit note",
        edit_for: "Edit note for %{name}",
      },
      deleted: "Note deleted",
      empty: "No notes yet",
      author_added: "%{name} added a note",
      you_added: "You added a note",
      me: "Me",
      list: {
        error_loading: "Error loading notes",
      },
      note_for_contact: "Note for %{name}",
      stepper: {
        hint: "Go to a contact page and add a note",
      },
      added: "Note added",
      inputs: {
        add_note: "Add a note",
        options_hint: "(attach files, or change details)",
        show_options: "Show options",
      },
      actions: {
        attach_document: "Attach document",
      },
      validation: {
        note_or_attachment_required: "A note or an attachment is required",
      },
    },
    sales: {
      name: "User |||| Users",
      fields: {
        first_name: "First name",
        last_name: "Last name",
        email: "Email",
        administrator: "Admin",
        disabled: "Disabled",
      },
      create: {
        error: "An error occurred while creating the user.",
        success:
          "User created. They will soon receive an email to set their password.",
        title: "Create a new user",
      },
      edit: {
        error: "An error occurred. Please try again.",
        record_not_found: "Record not found",
        success: "User updated successfully",
        title: "Edit %{name}",
      },
      action: {
        new: "New user",
      },
    },
    projects: {
      name: "Project |||| Projects",
      forcedCaseName: "Project",
      fields: {
        name: "Project name",
        description: "Description",
        company_id: "Customer",
        sales_id: "Project owner",
        created_at: "Created at",
        archived_at: "Archived at",
      },
      action: {
        create: "Create project",
        edit: "Edit project",
        new: "New project",
        show: "Show project",
      },
      empty: {
        title: "No projects found",
        description: "Your project list is empty.",
      },
    },
    work_items: {
      name: "Task |||| Tasks",
      forcedCaseName: "Task",
      fields: {
        title: "Title",
        description: "Description",
        attachments: "Attachments",
        project_id: "Project",
        section_id: "Status",
        parent_task_id: "Parent task",
        assignee_id: "Assignee",
        priority: "Priority",
        due_date: "Due date",
        completed_at: "Completed at",
        created_at: "Created at",
        status: "Status",
      },
      action: {
        create: "Create task",
        edit: "Edit task",
        new: "New task",
        show: "Show task",
      },
      quick: {
        description: "Create a task. More fields are in the full form.",
        more_fields: "More fields",
      },
      priority: {
        low: "Low",
        normal: "Normal",
        high: "High",
        urgent: "Urgent",
      },
      status: {
        open: "Open",
        done: "Completed",
        open_one: "Open",
        done_one: "Completed",
      },
      empty: {
        title: "No tasks found",
        description: "Your task list is empty.",
      },
      notification: {
        time_not_saved:
          "The task was created, but the tracked time was not saved.",
      },
    },
    sections: {
      name: "Status |||| Statuses",
    },
    field_defs: {
      name: "Custom field |||| Custom fields",
      fields: {
        name: "Field name",
        entity: "Belongs to",
        type: "Type",
        options: "Options",
        hidden: "Hidden",
      },
      action: {
        create: "Create field",
        edit: "Edit field",
        new: "New field",
      },
      empty: {
        title: "No custom fields",
        description:
          "Create fields for customers, projects, tasks or products.",
      },
    },
    products: {
      name: "Product |||| Products",
      fields: {
        product_number: "Product no.",
        name: "Name",
        description: "Description",
        unit: "Unit",
        purchase_price: "Purchase price excl. VAT",
        sales_price: "Sales price excl. VAT",
        sales_price_incl_vat: "Sales price incl. VAT",
        vat_rate: "VAT rate (%)",
        currency: "Currency",
        is_active: "Active",
        image: "Product image",
      },
      action: {
        create: "Create product",
        edit: "Edit product",
        new: "New product",
      },
      empty: {
        title: "No products",
        description: "Create products and services for customer pricing.",
      },
      status: {
        active: "Active",
        inactive: "Inactive",
      },
    },
    company_products: {
      name: "Customer product |||| Customer products",
      fields: {
        product_id: "Product",
        quantity: "Quantity",
        price: "Agreed price excl. VAT",
        cost: "Cost price",
        billing_interval: "Billing",
        start_date: "Start date",
        end_date: "End date",
        renewal_date: "Renews",
        last_maintained_on: "Last maintenance",
        last_invoiced_on: "Last invoiced",
        last_invoice_ref: "Invoice",
        note: "Note",
      },
    },
    tasks: {
      name: "Reminder |||| Reminders",
      forcedCaseName: "Reminder",
      fields: {
        text: "Description",
        due_date: "Due date",
        type: "Type",
        contact_id: "Contact",
        due_short: "due",
      },
      action: {
        add: "Add task",
        create: "Create task",
        edit: "Edit task",
      },
      actions: {
        postpone_next_week: "Postpone to next week",
        postpone_tomorrow: "Postpone to tomorrow",
        title: "task actions",
      },
      added: "Task added",
      deleted: "Task deleted successfully",
      dialog: {
        create: "Create task",
        create_for: "Create task for %{name}",
      },
      sheet: {
        edit: "Edit task",
        edit_for: "Edit task for %{name}",
      },
      empty: "No tasks yet",
      empty_list_hint: "Tasks added to your contacts will appear here.",
      filters: {
        later: "Later",
        overdue: "Overdue",
        this_week: "This week",
        today: "Today",
        tomorrow: "Tomorrow",
        with_pending: "With pending tasks",
      },
      regarding_contact: "(Re: %{name})",
      updated: "Task updated",
    },
    tags: {
      name: "Tag |||| Tags",
      action: {
        add: "Add tag",
        create: "Create new tag",
      },
      dialog: {
        color: "Color",
        create_title: "Create a new tag",
        edit_title: "Edit tag",
        name_label: "Tag name",
        name_placeholder: "Enter tag name",
      },
    },
  },
  crm: {
    agreements: {
      title: "Recurring agreements",
      nav: "Agreements",
      description:
        "All customers' recurring products and services. Add renewal, last maintenance and custom fields on the agreement.",
      kpi: {
        active: "Active agreements",
        customers: "Customers",
        renewal: "Renewing within %{days} days",
        maintenance: "Not maintained in %{months} months",
      },
      search: "Search customer, product or field",
      show: {
        all: "All",
        renewal: "Renewing soon",
        maintenance: "Needs maintenance",
      },
      columns: {
        company: "Customer",
        amount: "Price",
        interval: "Period",
      },
      maintained: "Maintenance recorded today",
      mark_maintained: "Maintenance done today",
      today: "Today",
      intervals: {
        one_time: "One-time",
        monthly: "Monthly",
        quarterly: "Quarterly",
        semiannual: "Every six months",
        yearly: "Yearly",
      },
    },
    quotes: {
      title: "Quotes",
      create: "Create quote",
      needs_lines: "Add product lines first.",
      empty: "No quotes yet.",
      viewed: "Opened by the customer",
      expired: "Expired",
      dialog_title: "Quote #%{number}",
      draft_hint:
        "Only you can see the draft. The customer sees the quote once you mark it as sent.",
      viewed_at: "Opened by the customer %{date}.",
      locked_hint:
        "Sent quotes are locked. Move it back to draft to change it.",
      fields: {
        title: "Title",
        intro: "Introduction",
        lines: "Lines",
        valid_until: "Valid until",
        terms: "Terms",
      },
      lines_refreshed: "Lines copied from the product lines again",
      refresh_lines: "Copy product lines again",
      delete: "Delete draft",
      edit: "Edit",
      preview: "Preview",
      copy_link: "Copy link",
      open_pdf: "PDF",
      saved: "Quote saved",
      mark_sent: "Mark as sent",
      back_to_draft: "Back to draft",
      mark_declined: "Declined",
      mark_accepted: "Accepted",
      back_to_sent: "Back to sent",
      link_copied: "Link to the quote copied",
      sent_done:
        "Quote sent. Copy the link or download the PDF for the customer.",
      totals: {
        one_time: "One-time",
        monthly: "Per month",
        first_year: "First year",
      },
      document: {
        label: "Quote",
        date: "Date",
        valid_until: "Valid until",
        to: "To",
        description: "Description",
        quantity: "Qty",
        unit_price: "Price",
        interval: "Period",
        amount: "Amount",
        vat: "All prices exclude VAT.",
        terms: "Terms",
        contact: "Questions about the quote? Contact",
      },
      public: {
        loading: "Loading the quote …",
        missing_title: "Quote not found",
        missing:
          "The link is wrong, or the quote has been withdrawn. Contact the person who sent you the link.",
        print: "Download as PDF",
      },
      status: {
        draft: "Draft",
        sent: "Sent",
        accepted: "Accepted",
        declined: "Declined",
      },
    },
    summary: {
      title: "Customer status",
      owner: "Owner",
      no_owner: "No owner",
      last_contact: "Last contact",
      no_contact: "No contact recorded",
      company_note: "Customer note",
      contact_note: "Contact note",
      next_step: "Next step",
      no_next_step: "Not set",
      overdue: "Overdue",
      open_deals:
        "%{smart_count} open opportunity |||| %{smart_count} open opportunities",
      active_projects:
        "%{smart_count} active project |||| %{smart_count} active projects",
    },
    contact_log: {
      title: "Log contact",
      description:
        "The contact counts as last contact and shows in the timeline.",
      type: "Type",
      when: "When",
      contact: "Contact person",
      deal: "Opportunity",
      none: "None selected",
      note: "What was said",
      with_next_step: "Set a new next step",
      save: "Log",
      saved: "Contact logged",
      next_step_failed:
        "The contact was saved, but the next step was not: %{error}",
      types: {
        call: "Call",
        meeting: "Meeting",
        email: "Email",
        other: "Other contact",
      },
    },
    next_step: {
      dialog_title: "Next step",
      dialog_description: "The previous step stays in the history.",
      mode_step: "Next step",
      mode_none: "No planned step",
      description: "What happens next",
      description_placeholder: "E.g. send a quote for the new website",
      owner: "Owner",
      no_owner: "None",
      due_date: "Date",
      reason: "Reason",
      reason_placeholder: "E.g. waiting for budget in January",
      history: "History",
      none_with_reason: "No planned step: %{reason}",
      outcome_done: "done",
      outcome_replaced: "replaced",
      saved: "Next step saved",
      completed: "Next step completed",
      complete: "Complete",
      change: "Change",
      set: "Set next step",
    },
    action: {
      reset_password: "Reset Password",
    },
    auth: {
      first_name: "First name",
      last_name: "Last name",
      confirm_password: "Confirm password",
      confirmation_required:
        "Please follow the link we just sent you by email to confirm your account.",
      recovery_email_sent:
        "If you're a registered user, you should receive a password recovery email shortly.",
      link_expired:
        "That link has expired or was already used. Request a new one to set your password.",
      or: "or",
      sign_in_failed: "Failed to log in.",
      sign_in_google_workspace: "Sign in with Google Workplace",
      sign_in_subtitle: "Sign in to continue to %{title}",
      signing_in: "Signing in …",
      signup: {
        create_account: "Create account",
        create_first_user:
          "Create the first user account to complete the setup.",
        creating: "Creating...",
        initial_user_created: "Initial user successfully created",
      },
      welcome_back: "Welcome back",
      welcome_title: "Welcome to Mimin",
    },
    common: {
      activity: "Activity",
      added: "added",
      details: "Details",
      last_activity_with_date: "last activity %{date}",
      load_more: "Load more",
      misc: "Misc",
      past: "Past",
      read_more: "Read more",
      retry: "Retry",
      show_less: "Show less",
      copied: "Copied!",
      copy: "Copy",
      loading: "Loading...",
      me: "Me",
      task_count: "%{smart_count} task |||| %{smart_count} tasks",
    },
    changelog: {
      title: "Changelog",
    },
    tutorial: {
      title: "Guide",
    },
    quick_create: {
      new: "New",
      company_description:
        "Create the company with basic information. More details can be filled in on the company page later.",
      contact_description:
        "Create the contact with basic information. More details can be filled in on the contact page later.",
    },
    activity: {
      added_company: "%{name} added company",
      you_added_company: "You added company",
      added_contact: "%{name} added",
      you_added_contact: "You added",
      added_note: "%{name} added a note about",
      you_added_note: "You added a note about",
      added_note_about_deal: "%{name} added a note about opportunity",
      you_added_note_about_deal: "You added a note about opportunity",
      added_deal: "%{name} added opportunity",
      you_added_deal: "You added opportunity",
      at_company: "at",
      to: "to",
      load_more: "Load more activity",
    },
    csv_import: {
      file: "CSV file",
      start: "Start import",
      stop: "Stop import",
      another: "Import another file",
      reading: "Reading the file …",
      running: "The import is running. Do not close this tab.",
      cancelled: "The import was stopped. Rows already saved are kept.",
      read_error: "Could not read the file: %{message}",
      rows_done: "%{done} of %{total} rows processed",
      created: "Created",
      updated: "Updated",
      skipped: "Skipped",
      failed: "Failed",
      problems: "Rows that were not imported",
      line: "Line %{line}",
      more_problems:
        "… and %{smart_count} more row |||| … and %{smart_count} more rows",
      unknown_error: "Unknown error",
      reasons: {
        no_name: "Missing name",
        no_name_or_email: "Missing both name and email",
        invalid_email: "Invalid email address: %{email}",
        duplicate_email: "A contact with %{email} already exists",
        duplicate_customer_number:
          "Customer number %{number} appears more than once in the file",
        archived: "%{name} is archived and is not changed",
      },
    },
    dashboard: {
      deals_chart: "Upcoming Opportunity Revenue",
      deals_pipeline: "Pipeline",
      latest_activity: "Latest Activity",
      latest_activity_error: "Error loading latest activity",
      latest_notes: "My Latest Notes",
      latest_notes_added_ago: "added %{timeAgo}",
      stepper: {
        install: "Install Mimin",
        progress: "%{step}/3 done",
        whats_next: "What's next?",
      },
      upcoming_tasks: "Upcoming Tasks",
    },
    header: {},
    image_editor: {
      change: "Change",
      drop_hint: "Drop a file to upload, or click to select it.",
      editable_content: "Editable content",
      title: "Upload and resize image",
      update_image: "Update Image",
    },
    settings: {
      about: "About",
      companies: {
        sectors: "Sectors",
      },
      dark_mode_logo: "Dark Mode Logo",
      deals: {
        categories: "Categories",
        currency: "Currency",
        pipeline_help: "Select which stages should count as pipeline.",
        pipeline_statuses: "Pipeline Statuses",
        stages: "Stages",
      },
      light_mode_logo: "Light Mode Logo",
      notes: {
        statuses: "Statuses",
      },
      reset_defaults: "Reset to Defaults",
      save_error: "Failed to save configuration",
      saved: "Configuration saved successfully",
      saving: "Saving...",
      tasks: {
        types: "Types",
      },
      preferences: "Preferences",
      title: "Settings",
      app_title: "App Title",
      groups: {
        generelt: "General",
        kunder: "Customers and projects",
        salg: "Sales and pipeline",
        leads: "Leads and intake",
        epost: "Email and texts",
        fakturering: "Billing",
      },
      sections: {
        branding: "Branding",
        prospecting: "Prospecting",
        billing: "Billing",
      },
      prospecting: {
        fit_help:
          "Who fits as a customer. This reorders the prospect list immediately — no rescan needed. It does not change what was found on the websites, only which findings get prioritised.",
        fit_neutral:
          "Anything you do not list is neutral, not unwanted. To actively deprioritise something, list it here with the weight «Avoid».",
        sectors: "Sectors",
        sectors_help:
          "Industry code as digits, as shown in the prospect filter. Two digits match the whole sector («47» = retail); more digits match narrower and take precedence («47.11»).",
        municipalities: "Preferred municipalities",
        municipalities_help:
          "Municipality numbers, comma separated — for example «0301, 3201, 3203». Listed municipalities get a small lift (×1.10). The others are not penalised.",
        size: "Size",
        size_help:
          "Number of employees. Below the first number is the core target group (full weight). From there the prospect is damped gradually: ×0.85, then ×0.60 from the next threshold and ×0.30 from the last. Large companies are not filtered out — they end up at the bottom.",
        core_under: "Core target below",
        large_from: "Large from",
        huge_from: "Very large from",
        platforms: "Platforms",
        platforms_help:
          "The platform the prospect's website runs on today. Include the ones you would most like to take over.",
      },
      validation: {
        duplicate: "Duplicate %{display_name}: %{items}",
        in_use:
          "Cannot remove %{display_name} that are still used by opportunities: %{items}",
        validating: "Validating\u2026",
        entities: {
          categories: "categories",
          stages: "stages",
        },
      },
    },
    theme: {
      dark: "Dark",
      label: "Theme",
      light: "Light",
      system: "System",
      toggle: "Toggle theme",
    },
    sidebar: {
      label: "Sidebar",
      mobile_description: "Displays the mobile sidebar.",
      toggle: "Toggle sidebar",
    },
    language: "Language",
    navigation: {
      label: "CRM navigation",
    },
    profile: {
      inbound: {
        description:
          "You can start sending emails to your server's inbound email address, e.g. by adding it to the %{field} field. Mimin will process the emails and add notes to the corresponding contacts.",
        title: "Inbound email",
      },
      password: {
        change: "Change password",
      },
      password_reset_sent:
        "A reset password email has been sent to your email address",
      record_not_found: "Record not found",
      title: "Profile",
      updated: "Your profile has been updated",
      update_error: "An error occurred. Please try again",
    },
    validation: {
      invalid_url: "Must be a valid URL",
      invalid_linkedin_url: "URL must be from linkedin.com",
    },
  },
} as const;

type MessageSchema<T> = {
  [K in keyof T]: T[K] extends string
    ? string
    : T[K] extends Record<string, unknown>
      ? MessageSchema<T[K]>
      : never;
};

type DeepPartial<T> = {
  [K in keyof T]?: T[K] extends Record<string, unknown>
    ? DeepPartial<T[K]>
    : T[K];
};

export type CrmMessages = MessageSchema<typeof englishCrmMessages>;
export type PartialCrmMessages = DeepPartial<CrmMessages>;
